import { Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSkeleton,
  AccordionTitle,
  Issue,
  IssueDrawerContent,
  IssuesTable,
  SignalRGroup,
  useDrawer,
  ViewList,
} from "../../../../components";
import { ISSUE_STATUSES, UserIssuesGroupByOption } from "../../../../constants";
import { useSession, useSignalR } from "../../../../hooks";
import { IssueSummaryDto, queryKeys, useGetIssues } from "../../../../http";
import { useUserImplementationSettings } from "../hooks";

interface Props {
  userGuid: string;
  isTableView: boolean;
}

const UserIssuesList = (props: Props) => {
  const { userId: sessionUserGuid } = useSession();
  const { settings } = useUserImplementationSettings(props.userGuid);
  const { data, isLoading } = useGetIssues(undefined, props.userGuid, {
    filters: {
      status: settings.issuesFilterByStatusOptions?.map((x) => x.toString()) ?? null,
    },
    sortProperty: settings.issuesSortByOption,
    sortDirection: settings.issuesSortByDirection,
  });
  const [groupedIssues, setGroupedIssues] = useState<Map<string, IssueSummaryDto[]> | undefined>(undefined);
  const { showDrawer } = useDrawer();
  const signalR = useSignalR();
  const queryClient = useQueryClient();
  const theme = useTheme();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.UserIssues(props.userGuid));
    return () => signalR.leaveGroup(SignalRGroup.UserIssues(props.userGuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userGuid]);

  useEffect(() => {
    const handleShouldRefetchUserIssues = (userGuid: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, userGuid));
    };

    signalR.onShouldRefetchUserIssues(handleShouldRefetchUserIssues);
    return () => signalR.offShouldRefetchUserIssues(handleShouldRefetchUserIssues);
  }, [queryClient, signalR]);

  const groupByFunction = useCallback(
    (issue: IssueSummaryDto) => {
      switch (settings.issuesGroupByOption) {
        case UserIssuesGroupByOption.Team:
          return issue.assignedTeam?.name ?? "No Team";
        case UserIssuesGroupByOption.Status:
          return ISSUE_STATUSES.find((x) => x.id === issue.currentStatus)?.name;
        case UserIssuesGroupByOption.None:
        default:
          return "All";
      }
    },
    [settings.issuesGroupByOption],
  );

  const groupSortByFunction = useCallback(
    (groupName: string) => {
      switch (settings.issuesGroupByOption) {
        case UserIssuesGroupByOption.Status:
          return _.sortBy(ISSUE_STATUSES, (x) => x.sortOrder)
            .map((x) => x.name)
            .indexOf(groupName);
        case UserIssuesGroupByOption.Team:
          return [groupName === "No Team", groupName];
        default:
          return groupName;
      }
    },
    [settings.issuesGroupByOption],
  );

  useEffect(() => {
    if (isLoading) {
      // Clear local state when loading new data
      setGroupedIssues(undefined);
    }
  }, [isLoading]);

  useEffect(() => {
    if (data !== undefined) {
      // Group issues
      const issues = _.groupBy(data.issues, groupByFunction);

      // Sort issue group names
      const sortedGroupNames = _.sortBy(_.keys(issues), groupSortByFunction);

      // Create a map of sorted groups
      const sortedGroupedIssues = sortedGroupNames.reduce((groupedIssues, groupName) => {
        groupedIssues.set(groupName, issues[groupName]);
        return groupedIssues;
      }, new Map());

      setGroupedIssues(sortedGroupedIssues);
    }
  }, [data, groupByFunction, groupSortByFunction]);

  function handleIssueClicked(issue: IssueSummaryDto) {
    showDrawer({
      content: <IssueDrawerContent issueGuid={issue.guid} />,
    });
  }

  if (isLoading || groupedIssues === undefined) {
    return <AccordionSkeleton isTableView={props.isTableView} />;
  }

  return (
    <Stack spacing={0.5}>
      {groupedIssues.size === 0 ? (
        <Typography variant="placeholder" sx={{ px: 0.5 }}>
          Nothing here yet.
        </Typography>
      ) : (
        Array.from(groupedIssues.keys()).map((groupName, i) => (
          <Accordion
            key={i}
            title={(expanded) => (
              <AccordionTitle
                title={groupName}
                subtitle={expanded ? undefined : `(${groupedIssues.get(groupName)?.length})`}
              />
            )}
            color={theme.palette.secondary.main}
            accordionDetailsSx={{ px: props.isTableView ? 0 : 0.5, pb: props.isTableView ? 0 : 0.5 }}
          >
            {props.isTableView ? (
              <IssuesTable
                tableId={`userIssuesList-${props.userGuid}`}
                issues={groupedIssues.get(groupName) ?? []}
                enableActions={sessionUserGuid === props.userGuid}
              />
            ) : (
              <ViewList
                dataSource={groupedIssues.get(groupName)}
                renderItem={(issue) => (
                  <Issue
                    key={issue.guid}
                    issue={issue}
                    onClick={() => handleIssueClicked(issue)}
                    isFocused={false}
                    sx={{ px: 0.5 }}
                  />
                )}
              />
            )}
          </Accordion>
        ))
      )}
    </Stack>
  );
};

export { UserIssuesList };
